/**
 * Pivot Points Series Component
 *
 * Renders 7 lines for Pivot Points in two modes:
 * - Normal mode: Horizontal line segments that change at period boundaries
 * - Continuous mode: Connected flowing lines that follow recalculated values
 *
 * Lines: Pivot (central), R1/R2/R3 (resistance), S1/S2/S3 (support)
 */

import React, { Component } from "react";
import PropTypes from "prop-types";
import GenericChartComponent from "../GenericChartComponent";
import { getAxisCanvas, getMouseCanvas } from "../GenericComponent";
import {
  hexToRGBA,
  getStrokeDasharray,
  strokeDashTypes,
  isDefined,
} from "../utils";

class PivotPointsSeries extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.drawStaticAreasCanvas = this.drawStaticAreasCanvas.bind(this);
    this.drawLinesCanvas = this.drawLinesCanvas.bind(this);
    this.renderStaticAreasSVG = this.renderStaticAreasSVG.bind(this);
    this.renderLinesSVG = this.renderLinesSVG.bind(this);
    this.isHover = this.isHover.bind(this);
  }

  isHover(moreProps) {
    const { highlightOnHover, yAccessor, hoverTolerance } = this.props;
    if (!highlightOnHover) return false;

    const {
      mouseXY,
      currentItem,
      chartConfig: { yScale, origin },
    } = moreProps;
    if (!mouseXY || !currentItem) return false;

    // Get pivot data at current mouse X position
    const pivotData = yAccessor(currentItem);
    if (!pivotData || pivotData.pivot == null) return false;

    // Check if mouse Y is within tolerance of any pivot level
    let [, y] = mouseXY;
    y += origin[1];
    const levels = ["pivot", "r1", "r2", "r3", "s1", "s2", "s3"];

    for (const level of levels) {
      if (pivotData[level] == null) continue;
      const lineY = yScale(pivotData[level]) + origin[1];
      if (Math.abs(y - lineY) <= hoverTolerance) return true;
    }
    return false;
  }

  drawOnCanvas(ctx, moreProps) {
    const { yAccessor, continuous, staticAreas } = this.props;
    const {
      strokePivot,
      strokeR1,
      strokeR2,
      strokeR3,
      strokeS1,
      strokeS2,
      strokeS3,
    } = this.props;

    const {
      xScale,
      chartConfig: { yScale, width },
      plotData,
      xAccessor,
    } = moreProps;

    if (!plotData || plotData.length === 0) return;

    if (continuous) {
      // Continuous mode: Draw connected flowing lines
      this.drawContinuousLine(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        "pivot",
        strokePivot,
      );
      this.drawContinuousLine(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        "r1",
        strokeR1,
      );
      this.drawContinuousLine(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        "r2",
        strokeR2,
      );
      this.drawContinuousLine(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        "r3",
        strokeR3,
      );
      this.drawContinuousLine(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        "s1",
        strokeS1,
      );
      this.drawContinuousLine(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        "s2",
        strokeS2,
      );
      this.drawContinuousLine(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        "s3",
        strokeS3,
      );
    } else {
      // Normal mode: Draw static areas first (behind lines), then lines on top
      if (staticAreas) {
        this.drawStaticAreas(
          ctx,
          plotData,
          xAccessor,
          yAccessor,
          xScale,
          yScale,
          width,
        );
      }

      // Draw horizontal line segments
      this.drawLevel(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        "pivot",
        strokePivot,
      );
      this.drawLevel(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        "r1",
        strokeR1,
      );
      this.drawLevel(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        "r2",
        strokeR2,
      );
      this.drawLevel(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        "r3",
        strokeR3,
      );
      this.drawLevel(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        "s1",
        strokeS1,
      );
      this.drawLevel(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        "s2",
        strokeS2,
      );
      this.drawLevel(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        "s3",
        strokeS3,
      );
    }
  }

  // Draw only static areas (for separate component that doesn't redraw on mousemove)
  drawStaticAreasCanvas(ctx, moreProps) {
    const { yAccessor, continuous } = this.props;
    const {
      xScale,
      chartConfig: { yScale, width },
      plotData,
      xAccessor,
    } = moreProps;

    if (!plotData || plotData.length === 0) return;

    if (continuous) {
      // In continuous mode, draw filled areas between the flowing lines
      this.drawContinuousAreas(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
      );
    } else {
      // In normal mode, draw segmented areas
      this.drawStaticAreas(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
      );
    }
  }

  drawContinuousAreas(ctx, plotData, xAccessor, yAccessor, xScale, yScale) {
    const zones = [
      { top: "r3", bottom: "r2", color: "rgba(139, 69, 61, 0.12)" },
      { top: "r2", bottom: "r1", color: "rgba(139, 69, 61, 0.10)" },
      { top: "r1", bottom: "pivot", color: "rgba(139, 69, 61, 0.08)" },
      { top: "pivot", bottom: "s1", color: "rgba(69, 139, 61, 0.08)" },
      { top: "s1", bottom: "s2", color: "rgba(69, 139, 61, 0.10)" },
      { top: "s2", bottom: "s3", color: "rgba(69, 139, 61, 0.12)" },
    ];

    for (const zone of zones) {
      this.drawContinuousZone(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        zone.top,
        zone.bottom,
        zone.color,
      );
    }
  }

  drawContinuousZone(
    ctx,
    plotData,
    xAccessor,
    yAccessor,
    xScale,
    yScale,
    topLevel,
    bottomLevel,
    fillColor,
  ) {
    // Collect points for top and bottom lines
    const topPoints = [];
    const bottomPoints = [];

    for (let i = 0; i < plotData.length; i++) {
      const d = plotData[i];
      const pivotData = yAccessor(d);

      if (
        !pivotData ||
        !isDefined(pivotData[topLevel]) ||
        !isDefined(pivotData[bottomLevel])
      ) {
        continue;
      }

      const x = xScale(xAccessor(d));
      const topY = yScale(pivotData[topLevel]);
      const bottomY = yScale(pivotData[bottomLevel]);

      topPoints.push({ x, y: topY });
      bottomPoints.push({ x, y: bottomY });
    }

    if (topPoints.length < 2) return;

    // Draw filled polygon between top and bottom lines
    ctx.fillStyle = fillColor;
    ctx.beginPath();

    // Draw top line left to right
    ctx.moveTo(topPoints[0].x, topPoints[0].y);
    for (let i = 1; i < topPoints.length; i++) {
      ctx.lineTo(topPoints[i].x, topPoints[i].y);
    }

    // Draw bottom line right to left
    for (let i = bottomPoints.length - 1; i >= 0; i--) {
      ctx.lineTo(bottomPoints[i].x, bottomPoints[i].y);
    }

    ctx.closePath();
    ctx.fill();
  }

  // Draw only lines (for component that redraws on mousemove)
  drawLinesCanvas(ctx, moreProps) {
    const { yAccessor, continuous } = this.props;
    const {
      strokePivot,
      strokeR1,
      strokeR2,
      strokeR3,
      strokeS1,
      strokeS2,
      strokeS3,
    } = this.props;

    const {
      xScale,
      chartConfig: { yScale, width },
      plotData,
      xAccessor,
      hovering,
    } = moreProps;

    if (!plotData || plotData.length === 0) return;

    if (continuous) {
      this.drawContinuousLine(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        "pivot",
        strokePivot,
        hovering,
      );
      this.drawContinuousLine(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        "r1",
        strokeR1,
        hovering,
      );
      this.drawContinuousLine(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        "r2",
        strokeR2,
        hovering,
      );
      this.drawContinuousLine(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        "r3",
        strokeR3,
        hovering,
      );
      this.drawContinuousLine(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        "s1",
        strokeS1,
        hovering,
      );
      this.drawContinuousLine(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        "s2",
        strokeS2,
        hovering,
      );
      this.drawContinuousLine(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        "s3",
        strokeS3,
        hovering,
      );
    } else {
      this.drawLevel(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        "pivot",
        strokePivot,
        hovering,
      );
      this.drawLevel(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        "r1",
        strokeR1,
        hovering,
      );
      this.drawLevel(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        "r2",
        strokeR2,
        hovering,
      );
      this.drawLevel(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        "r3",
        strokeR3,
        hovering,
      );
      this.drawLevel(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        "s1",
        strokeS1,
        hovering,
      );
      this.drawLevel(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        "s2",
        strokeS2,
        hovering,
      );
      this.drawLevel(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        "s3",
        strokeS3,
        hovering,
      );
    }
  }

  drawStaticAreas(ctx, plotData, xAccessor, yAccessor, xScale, yScale, width) {
    // Draw static area zones between pivot levels
    // Resistance zones: R1-Pivot, R2-R1, R3-R2 (reddish)
    // Support zones: Pivot-S1, S1-S2, S2-S3 (greenish)

    const zones = [
      { top: "r3", bottom: "r2", color: "rgba(139, 69, 61, 0.12)" }, // Darkest red
      { top: "r2", bottom: "r1", color: "rgba(139, 69, 61, 0.10)" }, // Medium red
      { top: "r1", bottom: "pivot", color: "rgba(139, 69, 61, 0.08)" }, // Light red
      { top: "pivot", bottom: "s1", color: "rgba(69, 139, 61, 0.08)" }, // Light green
      { top: "s1", bottom: "s2", color: "rgba(69, 139, 61, 0.10)" }, // Medium green
      { top: "s2", bottom: "s3", color: "rgba(69, 139, 61, 0.12)" }, // Darkest green
    ];

    for (const zone of zones) {
      this.drawZone(
        ctx,
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        zone.top,
        zone.bottom,
        zone.color,
      );
    }
  }

  drawZone(
    ctx,
    plotData,
    xAccessor,
    yAccessor,
    xScale,
    yScale,
    width,
    topLevel,
    bottomLevel,
    fillColor,
  ) {
    let currentTopValue = null;
    let currentBottomValue = null;
    let segmentStartX = null;

    for (let i = 0; i < plotData.length; i++) {
      const d = plotData[i];
      const pivotData = yAccessor(d);

      if (
        !pivotData ||
        !isDefined(pivotData[topLevel]) ||
        !isDefined(pivotData[bottomLevel])
      ) {
        // End current segment if any
        if (currentTopValue !== null && segmentStartX !== null) {
          const segmentEndX =
            i > 0 ? xScale(xAccessor(plotData[i - 1])) : segmentStartX;
          const y1 = yScale(currentTopValue);
          const y2 = yScale(currentBottomValue);

          ctx.fillStyle = fillColor;
          ctx.fillRect(segmentStartX, y1, segmentEndX - segmentStartX, y2 - y1);

          currentTopValue = null;
          currentBottomValue = null;
          segmentStartX = null;
        }
        continue;
      }

      const topValue = pivotData[topLevel];
      const bottomValue = pivotData[bottomLevel];

      // Check if values changed (new period)
      if (currentTopValue !== topValue || currentBottomValue !== bottomValue) {
        // Draw previous segment
        if (currentTopValue !== null && segmentStartX !== null) {
          const currentX = xScale(xAccessor(d));
          const y1 = yScale(currentTopValue);
          const y2 = yScale(currentBottomValue);

          ctx.fillStyle = fillColor;
          ctx.fillRect(segmentStartX, y1, currentX - segmentStartX, y2 - y1);
        }

        // Start new segment
        currentTopValue = topValue;
        currentBottomValue = bottomValue;
        segmentStartX = xScale(xAccessor(d));
      }
    }

    // Draw final segment
    if (currentTopValue !== null && segmentStartX !== null) {
      const segmentEndX = xScale(xAccessor(plotData[plotData.length - 1]));
      const y1 = yScale(currentTopValue);
      const y2 = yScale(currentBottomValue);

      ctx.fillStyle = fillColor;
      ctx.fillRect(segmentStartX, y1, segmentEndX - segmentStartX, y2 - y1);
    }
  }

  drawContinuousLine(
    ctx,
    plotData,
    xAccessor,
    yAccessor,
    xScale,
    yScale,
    levelName,
    stroke,
    hovering = false,
  ) {
    if (!stroke || !stroke.color) return;

    // Set up stroke style
    ctx.strokeStyle = hexToRGBA(stroke.color, stroke.opacity);
    ctx.lineWidth = hovering ? stroke.width * 1.5 : stroke.width;
    const dashArray = getStrokeDasharray(stroke.dash);
    ctx.setLineDash(
      dashArray === "Solid" || !dashArray
        ? []
        : dashArray.split(",").map(Number),
    );

    ctx.beginPath();
    let firstPoint = true;

    for (let i = 0; i < plotData.length; i++) {
      const d = plotData[i];
      const pivotData = yAccessor(d);

      if (!pivotData || !isDefined(pivotData[levelName])) {
        continue;
      }

      const x = xScale(xAccessor(d));
      const y = yScale(pivotData[levelName]);

      if (firstPoint) {
        ctx.moveTo(x, y);
        firstPoint = false;
      } else {
        ctx.lineTo(x, y);
      }
    }

    ctx.stroke();
  }

  drawLevel(
    ctx,
    plotData,
    xAccessor,
    yAccessor,
    xScale,
    yScale,
    width,
    levelName,
    stroke,
    hovering = false,
  ) {
    if (!stroke || !stroke.color) return;

    // Set up stroke style
    ctx.strokeStyle = hexToRGBA(stroke.color, stroke.opacity);
    ctx.lineWidth = hovering ? stroke.width * 1.5 : stroke.width;
    const dashArray = getStrokeDasharray(stroke.dash);
    ctx.setLineDash(
      dashArray === "Solid" || !dashArray
        ? []
        : dashArray.split(",").map(Number),
    );

    let currentValue = null;
    let segmentStartX = null;
    let segmentCount = 0;

    for (let i = 0; i < plotData.length; i++) {
      const d = plotData[i];
      const pivotData = yAccessor(d);

      if (!pivotData || !isDefined(pivotData[levelName])) {
        // No data - end current segment if any
        if (currentValue !== null && segmentStartX !== null) {
          const segmentEndX =
            i > 0 ? xScale(xAccessor(plotData[i - 1])) : segmentStartX;
          const y = yScale(currentValue);

          ctx.beginPath();
          ctx.moveTo(segmentStartX, y);
          ctx.lineTo(segmentEndX, y);
          ctx.stroke();

          currentValue = null;
          segmentStartX = null;
        }
        continue;
      }

      const value = pivotData[levelName];

      // Check if value changed (new period)
      if (currentValue !== value) {
        // Draw previous segment if it exists
        if (currentValue !== null && segmentStartX !== null) {
          // Calculate bar width for visible lines on daily charts
          const currentX = xScale(xAccessor(d));
          const barWidth = i > 0 ? currentX - segmentStartX : 10;
          const segmentEndX = segmentStartX + Math.max(barWidth, 10);
          const y = yScale(currentValue);

          ctx.beginPath();
          ctx.moveTo(segmentStartX, y);
          ctx.lineTo(segmentEndX, y);
          ctx.stroke();
          segmentCount++;
        }

        // Start new segment
        currentValue = value;
        segmentStartX = xScale(xAccessor(d));
      }
    }

    // Draw final segment
    if (currentValue !== null && segmentStartX !== null) {
      const segmentEndX = xScale(xAccessor(plotData[plotData.length - 1]));
      const y = yScale(currentValue);

      ctx.beginPath();
      ctx.moveTo(segmentStartX, y);
      ctx.lineTo(segmentEndX, y);
      ctx.stroke();
    }
  }

  // SVG rendering for static areas only
  renderStaticAreasSVG(moreProps) {
    const { yAccessor, className, continuous } = this.props;
    const {
      xScale,
      chartConfig: { yScale, width },
      plotData,
      xAccessor,
    } = moreProps;

    if (!plotData || plotData.length === 0) return null;

    return (
      <g className={`${className}-static-areas`} pointerEvents="none">
        {continuous
          ? this.renderContinuousAreas(
              plotData,
              xAccessor,
              yAccessor,
              xScale,
              yScale,
            )
          : this.renderStaticAreas(
              plotData,
              xAccessor,
              yAccessor,
              xScale,
              yScale,
              width,
            )}
      </g>
    );
  }

  renderContinuousAreas(plotData, xAccessor, yAccessor, xScale, yScale) {
    const zones = [
      { top: "r3", bottom: "r2", color: "rgba(139, 69, 61, 0.12)" },
      { top: "r2", bottom: "r1", color: "rgba(139, 69, 61, 0.10)" },
      { top: "r1", bottom: "pivot", color: "rgba(139, 69, 61, 0.08)" },
      { top: "pivot", bottom: "s1", color: "rgba(69, 139, 61, 0.08)" },
      { top: "s1", bottom: "s2", color: "rgba(69, 139, 61, 0.10)" },
      { top: "s2", bottom: "s3", color: "rgba(69, 139, 61, 0.12)" },
    ];

    return zones.map((zone, idx) =>
      this.renderContinuousZone(
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        zone.top,
        zone.bottom,
        zone.color,
        idx,
      ),
    );
  }

  renderContinuousZone(
    plotData,
    xAccessor,
    yAccessor,
    xScale,
    yScale,
    topLevel,
    bottomLevel,
    fillColor,
    zoneIndex,
  ) {
    const topPoints = [];
    const bottomPoints = [];

    for (let i = 0; i < plotData.length; i++) {
      const d = plotData[i];
      const pivotData = yAccessor(d);

      if (
        !pivotData ||
        !isDefined(pivotData[topLevel]) ||
        !isDefined(pivotData[bottomLevel])
      ) {
        continue;
      }

      const x = xScale(xAccessor(d));
      const topY = yScale(pivotData[topLevel]);
      const bottomY = yScale(pivotData[bottomLevel]);

      topPoints.push({ x, y: topY });
      bottomPoints.push({ x, y: bottomY });
    }

    if (topPoints.length < 2) return null;

    // Create path: top line (left to right) then bottom line (right to left)
    let pathData = `M ${topPoints[0].x},${topPoints[0].y}`;
    for (let i = 1; i < topPoints.length; i++) {
      pathData += ` L ${topPoints[i].x},${topPoints[i].y}`;
    }
    for (let i = bottomPoints.length - 1; i >= 0; i--) {
      pathData += ` L ${bottomPoints[i].x},${bottomPoints[i].y}`;
    }
    pathData += " Z";

    return (
      <path
        key={`continuous-zone-${zoneIndex}`}
        d={pathData}
        fill={fillColor}
        pointerEvents="none"
      />
    );
  }

  // SVG rendering for lines only
  renderLinesSVG(moreProps) {
    const { yAccessor, className, continuous } = this.props;
    const {
      strokePivot,
      strokeR1,
      strokeR2,
      strokeR3,
      strokeS1,
      strokeS2,
      strokeS3,
    } = this.props;

    const {
      xScale,
      chartConfig: { yScale, width },
      plotData,
      xAccessor,
    } = moreProps;

    if (!plotData || plotData.length === 0) return null;

    if (continuous) {
      return (
        <g className={className}>
          {this.renderContinuousLine(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            "pivot",
            strokePivot,
          )}
          {this.renderContinuousLine(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            "r1",
            strokeR1,
          )}
          {this.renderContinuousLine(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            "r2",
            strokeR2,
          )}
          {this.renderContinuousLine(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            "r3",
            strokeR3,
          )}
          {this.renderContinuousLine(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            "s1",
            strokeS1,
          )}
          {this.renderContinuousLine(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            "s2",
            strokeS2,
          )}
          {this.renderContinuousLine(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            "s3",
            strokeS3,
          )}
        </g>
      );
    } else {
      return (
        <g className={className}>
          {this.renderLevel(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            width,
            "pivot",
            strokePivot,
          )}
          {this.renderLevel(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            width,
            "r1",
            strokeR1,
          )}
          {this.renderLevel(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            width,
            "r2",
            strokeR2,
          )}
          {this.renderLevel(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            width,
            "r3",
            strokeR3,
          )}
          {this.renderLevel(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            width,
            "s1",
            strokeS1,
          )}
          {this.renderLevel(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            width,
            "s2",
            strokeS2,
          )}
          {this.renderLevel(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            width,
            "s3",
            strokeS3,
          )}
        </g>
      );
    }
  }

  renderSVG(moreProps) {
    const { yAccessor, className, continuous, staticAreas } = this.props;
    const {
      strokePivot,
      strokeR1,
      strokeR2,
      strokeR3,
      strokeS1,
      strokeS2,
      strokeS3,
    } = this.props;

    const {
      xScale,
      chartConfig: { yScale, width },
      plotData,
      xAccessor,
    } = moreProps;

    if (!plotData || plotData.length === 0) return null;

    if (continuous) {
      return (
        <g className={className}>
          {this.renderContinuousLine(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            "pivot",
            strokePivot,
          )}
          {this.renderContinuousLine(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            "r1",
            strokeR1,
          )}
          {this.renderContinuousLine(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            "r2",
            strokeR2,
          )}
          {this.renderContinuousLine(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            "r3",
            strokeR3,
          )}
          {this.renderContinuousLine(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            "s1",
            strokeS1,
          )}
          {this.renderContinuousLine(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            "s2",
            strokeS2,
          )}
          {this.renderContinuousLine(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            "s3",
            strokeS3,
          )}
        </g>
      );
    } else {
      return (
        <g className={className}>
          {staticAreas && (
            <g pointerEvents="none">
              {this.renderStaticAreas(
                plotData,
                xAccessor,
                yAccessor,
                xScale,
                yScale,
                width,
              )}
            </g>
          )}
          {this.renderLevel(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            width,
            "pivot",
            strokePivot,
          )}
          {this.renderLevel(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            width,
            "r1",
            strokeR1,
          )}
          {this.renderLevel(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            width,
            "r2",
            strokeR2,
          )}
          {this.renderLevel(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            width,
            "r3",
            strokeR3,
          )}
          {this.renderLevel(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            width,
            "s1",
            strokeS1,
          )}
          {this.renderLevel(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            width,
            "s2",
            strokeS2,
          )}
          {this.renderLevel(
            plotData,
            xAccessor,
            yAccessor,
            xScale,
            yScale,
            width,
            "s3",
            strokeS3,
          )}
        </g>
      );
    }
  }

  renderStaticAreas(plotData, xAccessor, yAccessor, xScale, yScale, width) {
    const zones = [
      { top: "r3", bottom: "r2", color: "rgba(139, 69, 61, 0.12)" },
      { top: "r2", bottom: "r1", color: "rgba(139, 69, 61, 0.10)" },
      { top: "r1", bottom: "pivot", color: "rgba(139, 69, 61, 0.08)" },
      { top: "pivot", bottom: "s1", color: "rgba(69, 139, 61, 0.08)" },
      { top: "s1", bottom: "s2", color: "rgba(69, 139, 61, 0.10)" },
      { top: "s2", bottom: "s3", color: "rgba(69, 139, 61, 0.12)" },
    ];

    return zones.map((zone, idx) =>
      this.renderZone(
        plotData,
        xAccessor,
        yAccessor,
        xScale,
        yScale,
        width,
        zone.top,
        zone.bottom,
        zone.color,
        idx,
      ),
    );
  }

  renderZone(
    plotData,
    xAccessor,
    yAccessor,
    xScale,
    yScale,
    width,
    topLevel,
    bottomLevel,
    fillColor,
    zoneIndex,
  ) {
    const rects = [];
    let currentTopValue = null;
    let currentBottomValue = null;
    let segmentStartX = null;
    let segmentKey = 0;

    for (let i = 0; i < plotData.length; i++) {
      const d = plotData[i];
      const pivotData = yAccessor(d);

      if (
        !pivotData ||
        !isDefined(pivotData[topLevel]) ||
        !isDefined(pivotData[bottomLevel])
      ) {
        if (currentTopValue !== null && segmentStartX !== null) {
          const segmentEndX =
            i > 0 ? xScale(xAccessor(plotData[i - 1])) : segmentStartX;
          const y1 = yScale(currentTopValue);
          const y2 = yScale(currentBottomValue);

          rects.push(
            <rect
              key={`zone-${zoneIndex}-${segmentKey++}`}
              x={segmentStartX}
              y={y1}
              width={segmentEndX - segmentStartX}
              height={y2 - y1}
              fill={fillColor}
              pointerEvents="none"
            />,
          );

          currentTopValue = null;
          currentBottomValue = null;
          segmentStartX = null;
        }
        continue;
      }

      const topValue = pivotData[topLevel];
      const bottomValue = pivotData[bottomLevel];

      if (currentTopValue !== topValue || currentBottomValue !== bottomValue) {
        if (currentTopValue !== null && segmentStartX !== null) {
          const currentX = xScale(xAccessor(d));
          const y1 = yScale(currentTopValue);
          const y2 = yScale(currentBottomValue);

          rects.push(
            <rect
              key={`zone-${zoneIndex}-${segmentKey++}`}
              x={segmentStartX}
              y={y1}
              width={currentX - segmentStartX}
              height={y2 - y1}
              fill={fillColor}
              pointerEvents="none"
            />,
          );
        }

        currentTopValue = topValue;
        currentBottomValue = bottomValue;
        segmentStartX = xScale(xAccessor(d));
      }
    }

    // Final segment
    if (currentTopValue !== null && segmentStartX !== null) {
      const segmentEndX = xScale(xAccessor(plotData[plotData.length - 1]));
      const y1 = yScale(currentTopValue);
      const y2 = yScale(currentBottomValue);

      rects.push(
        <rect
          key={`zone-${zoneIndex}-${segmentKey++}`}
          x={segmentStartX}
          y={y1}
          width={segmentEndX - segmentStartX}
          height={y2 - y1}
          fill={fillColor}
          pointerEvents="none"
        />,
      );
    }

    return rects;
  }

  renderContinuousLine(
    plotData,
    xAccessor,
    yAccessor,
    xScale,
    yScale,
    levelName,
    stroke,
  ) {
    if (!stroke || !stroke.color) return null;

    const points = [];

    for (let i = 0; i < plotData.length; i++) {
      const d = plotData[i];
      const pivotData = yAccessor(d);

      if (!pivotData || !isDefined(pivotData[levelName])) {
        continue;
      }

      const x = xScale(xAccessor(d));
      const y = yScale(pivotData[levelName]);
      points.push(`${x},${y}`);
    }

    if (points.length === 0) return null;

    return (
      <polyline
        key={`${levelName}-continuous`}
        points={points.join(" ")}
        stroke={stroke.color}
        strokeWidth={stroke.width}
        strokeOpacity={stroke.opacity}
        strokeDasharray={getStrokeDasharray(stroke.dash)}
        fill="none"
      />
    );
  }

  renderLevel(
    plotData,
    xAccessor,
    yAccessor,
    xScale,
    yScale,
    width,
    levelName,
    stroke,
  ) {
    if (!stroke || !stroke.color) return null;

    const lines = [];
    let currentValue = null;
    let segmentStartX = null;
    let segmentKey = 0;

    for (let i = 0; i < plotData.length; i++) {
      const d = plotData[i];
      const pivotData = yAccessor(d);

      if (!pivotData || !isDefined(pivotData[levelName])) {
        // No data - end current segment if any
        if (currentValue !== null && segmentStartX !== null) {
          const segmentEndX =
            i > 0 ? xScale(xAccessor(plotData[i - 1])) : segmentStartX;
          const y = yScale(currentValue);

          lines.push(
            <line
              key={`${levelName}-${segmentKey++}`}
              x1={segmentStartX}
              y1={y}
              x2={segmentEndX}
              y2={y}
              stroke={stroke.color}
              strokeWidth={stroke.width}
              strokeOpacity={stroke.opacity}
              strokeDasharray={getStrokeDasharray(stroke.dash)}
            />,
          );

          currentValue = null;
          segmentStartX = null;
        }
        continue;
      }

      const value = pivotData[levelName];

      // Check if value changed (new period)
      if (currentValue !== value) {
        // Draw previous segment if it exists
        if (currentValue !== null && segmentStartX !== null) {
          const segmentEndX =
            i > 0 ? xScale(xAccessor(plotData[i - 1])) : segmentStartX;
          const y = yScale(currentValue);

          lines.push(
            <line
              key={`${levelName}-${segmentKey++}`}
              x1={segmentStartX}
              y1={y}
              x2={segmentEndX}
              y2={y}
              stroke={stroke.color}
              strokeWidth={stroke.width}
              strokeOpacity={stroke.opacity}
              strokeDasharray={getStrokeDasharray(stroke.dash)}
            />,
          );
        }

        // Start new segment
        currentValue = value;
        segmentStartX = xScale(xAccessor(d));
      }
    }

    // Draw final segment
    if (currentValue !== null && segmentStartX !== null) {
      const segmentEndX = xScale(xAccessor(plotData[plotData.length - 1]));
      const y = yScale(currentValue);

      lines.push(
        <line
          key={`${levelName}-${segmentKey++}`}
          x1={segmentStartX}
          y1={y}
          x2={segmentEndX}
          y2={y}
          stroke={stroke.color}
          strokeWidth={stroke.width}
          strokeOpacity={stroke.opacity}
          strokeDasharray={getStrokeDasharray(stroke.dash)}
        />,
      );
    }

    return lines;
  }

  render() {
    const { staticAreas, highlightOnHover, onHover, onUnHover } = this.props;

    const hoverProps =
      highlightOnHover || onHover || onUnHover
        ? {
            isHover: this.isHover,
            drawOn: ["mousemove", "pan", "drag"],
            canvasToDraw: getMouseCanvas,
          }
        : {
            drawOn: ["pan", "drag"],
            canvasToDraw: getAxisCanvas,
          };

    return (
      <>
        {/* Static areas - only redraw on pan, not on mousemove */}
        {staticAreas && (
          <GenericChartComponent
            canvasToDraw={getAxisCanvas}
            canvasDraw={this.drawStaticAreasCanvas}
            svgDraw={this.renderStaticAreasSVG}
            drawOn={["pan"]}
          />
        )}
        {/* Lines - redraw on pan and mousemove, with hover support */}
        <GenericChartComponent
          canvasDraw={this.drawLinesCanvas}
          svgDraw={this.renderLinesSVG}
          onClickWhenHover={this.props.onClick}
          onDoubleClickWhenHover={this.props.onDoubleClick}
          onContextMenuWhenHover={this.props.onContextMenu}
          onHover={this.props.onHover}
          onUnHover={this.props.onUnHover}
          {...hoverProps}
        />
      </>
    );
  }
}

PivotPointsSeries.propTypes = {
  className: PropTypes.string,
  yAccessor: PropTypes.func.isRequired,
  continuous: PropTypes.bool,
  staticAreas: PropTypes.bool,
  strokePivot: PropTypes.object,
  strokeR1: PropTypes.object,
  strokeR2: PropTypes.object,
  strokeR3: PropTypes.object,
  strokeS1: PropTypes.object,
  strokeS2: PropTypes.object,
  strokeS3: PropTypes.object,
  highlightOnHover: PropTypes.bool,
  hoverTolerance: PropTypes.number,
  onClick: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onContextMenu: PropTypes.func,
  onHover: PropTypes.func,
  onUnHover: PropTypes.func,
};

PivotPointsSeries.defaultProps = {
  className: "react-stockcharts-pivot-points-series",
  // Colors from ChartIQ
  strokePivot: { color: "#9C27B0", opacity: 1, width: 1, dash: "Solid" }, // Purple
  strokeR1: { color: "#B82C0B", opacity: 1, width: 1, dash: "Solid" }, // Dark red
  strokeR2: { color: "#E36460", opacity: 1, width: 1, dash: "Solid" }, // Light red
  strokeR3: { color: "#FFD0CF", opacity: 1, width: 1, dash: "Solid" }, // Pink
  strokeS1: { color: "#699158", opacity: 1, width: 1, dash: "Solid" }, // Dark green
  strokeS2: { color: "#B3D987", opacity: 1, width: 1, dash: "Solid" }, // Light green
  strokeS3: { color: "#D3E8AE", opacity: 1, width: 1, dash: "Solid" }, // Very light green
  highlightOnHover: true,
  hoverTolerance: 6,
};

export default PivotPointsSeries;
